import { useEffect } from 'react';
import type { BoardDoc } from '../doc/BoardDoc';
import { EDITABLE_TYPES } from '../doc/schema';
import { bringForward, bringToFront, sendBackward, sendToBack } from '../doc/zorder';
import {
  copySelection,
  createLinkAt,
  createNoteAt,
  cutSelection,
  looksLikeUrl,
  deleteSelection,
  duplicateSelection,
  nudgeSelection,
  pasteClipboard,
  selectAll,
  viewCenter,
  zoomBy,
  zoomTo,
  zoomToFit,
  zoomToSelection,
} from './actions';
import { importImageFiles, pickImageFiles } from './importImages';
import { boardIdFromUrl, createBoardCardAt } from './boardCard';
import { clipboardFromEvent, recentClipboard, rememberClipboard, snapshotForClipboard, writeToClipboardEvent } from './clipboard';
import type { PaneStore } from './paneStore';
import { setToolSettings, toolSettings } from './toolSettings';
import { groupSelection, toggleLockSelection, ungroupSelection } from './arrange';

/** The pane that last received a pointer event; keyboard shortcuts go there. */
let activePane: PaneStore | null = null;
export const setActivePane = (p: PaneStore) => {
  activePane = p;
};

function isTextTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
}

export function useKeyboard(board: BoardDoc, pane: PaneStore, readOnly = false): void {
  useEffect(() => {
    // The most recently mounted pane takes the keyboard until a pointer lands on another one.
    activePane = pane;
    const ctx = { board, pane };

    const onKeyDown = (e: KeyboardEvent) => {
      if (activePane !== pane) return;
      const s = pane.getState();
      if (isTextTarget(e.target)) {
        if (e.key === 'Escape') (e.target as HTMLElement).blur();
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      const NAV_KEYS = new Set(['+', '=', '-', '0', '1', '2', 'v', 'h', 'a', 'escape', '?']);
      if (readOnly && e.code !== 'Space' && !NAV_KEYS.has(key)) return;

      if (e.code === 'Space' && !e.repeat) {
        s.setSpaceHeld(true);
        e.preventDefault();
        return;
      }

      if (mod) {
        switch (key) {
          case 'z':
            if (e.shiftKey) board.undo.redo();
            else board.undo.undo();
            e.preventDefault();
            return;
          case 'y':
            board.undo.redo();
            e.preventDefault();
            return;
          case 'a':
            selectAll(ctx);
            e.preventDefault();
            return;
          case 'c':
            // no preventDefault: the browser's copy event follows and fills the system clipboard
            void copySelection(ctx);
            return;
          case 'x':
            void cutSelection(ctx);
            return;
          case 'v':
            // handled by the 'paste' event so system-clipboard images work too
            return;
          case 'd':
            void duplicateSelection(ctx);
            e.preventDefault();
            return;
          case 'g':
            if (e.shiftKey) ungroupSelection(ctx);
            else groupSelection(ctx);
            e.preventDefault();
            return;
          case 'l':
            toggleLockSelection(ctx);
            e.preventDefault();
            return;
          case "'":
            setToolSettings({ snap: !toolSettings.getState().snap });
            e.preventDefault();
            return;
          case '0':
            zoomTo(pane, 1);
            e.preventDefault();
            return;
          case '=':
          case '+':
            zoomBy(pane, 1.25);
            e.preventDefault();
            return;
          case '-':
            zoomBy(pane, 0.8);
            e.preventDefault();
            return;
          case '[':
            sendToBack(board, new Set(s.selection));
            e.preventDefault();
            return;
          case ']':
            bringToFront(board, new Set(s.selection));
            e.preventDefault();
            return;
        }
        return;
      }

      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          deleteSelection(ctx);
          e.preventDefault();
          return;
        case '?':
          window.dispatchEvent(new Event('vds:shortcuts'));
          return;
        case 'Escape':
          // first press clears what is selected or being edited; a second one returns to Select
          if (s.selection.size || s.editingId) {
            s.clearSelection();
            s.setEditing(null);
          } else {
            s.setTool('select');
          }
          return;
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'ArrowUp':
        case 'ArrowDown': {
          if (!s.selection.size) return;
          const d = e.shiftKey ? 10 : 1;
          const dx = e.key === 'ArrowLeft' ? -d : e.key === 'ArrowRight' ? d : 0;
          const dy = e.key === 'ArrowUp' ? -d : e.key === 'ArrowDown' ? d : 0;
          nudgeSelection(ctx, dx, dy);
          e.preventDefault();
          return;
        }
        case '[':
          sendBackward(board, new Set(s.selection));
          return;
        case ']':
          bringForward(board, new Set(s.selection));
          return;
        case '+':
        case '=':
          zoomBy(pane, 1.25);
          return;
        case '-':
          zoomBy(pane, 0.8);
          return;
        case '0':
          zoomTo(pane, 1);
          return;
        case '1':
          zoomToFit(ctx);
          return;
        case '2':
          zoomToSelection(ctx);
          return;
      }

      switch (key) {
        case 'v':
          s.setTool('select');
          return;
        case 'h':
          s.setTool('pan');
          return;
        case 'n':
          s.setTool('note');
          return;
        case 't':
          s.setTool('title');
          return;
        case 'x':
          s.setTool('richtext');
          return;
        case 'b':
          s.setTool('table');
          return;
        case 'd':
          s.setTool('todo');
          return;
        case 'k':
          s.setTool('kanban');
          return;
        case 's':
          s.setTool('shape');
          return;
        case 'f':
          s.setTool('frame');
          return;
        case 'p':
          setToolSettings({ penMode: 'pen' });
          s.setTool('draw');
          return;
        case 'm':
          setToolSettings({ penMode: 'highlighter' });
          s.setTool('draw');
          return;
        case 'e':
          setToolSettings({ penMode: 'eraser' });
          s.setTool('draw');
          return;
        case 'l':
          setToolSettings({ penMode: 'lasso' });
          s.setTool('draw');
          return;
        case 'i':
          void pickImageFiles().then((files) => importImageFiles(ctx, files));
          return;
        case 'enter': {
          const [only] = s.selection;
          if (only && s.selection.size === 1) {
            const item = board.index.get(only);
            if (item && EDITABLE_TYPES.has(item.type)) s.setEditing(only);
          }
          return;
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') pane.getState().setSpaceHeld(false);
    };

    const onPaste = (e: ClipboardEvent) => {
      if (activePane !== pane || isTextTarget(e.target) || readOnly) return;
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length) {
        e.preventDefault();
        void importImageFiles(ctx, files, viewCenter(pane));
        return;
      }
      // items copied on a board: this tab, another tab, or the system clipboard
      const items = clipboardFromEvent(e);
      if (items) {
        e.preventDefault();
        void pasteClipboard(ctx, undefined, items);
        return;
      }
      const text = (e.clipboardData?.getData('text/plain') ?? '').trim();
      if (!text) return;
      e.preventDefault();
      // a link to one of this app's boards becomes a board card rather than a website card
      const linkedBoard = board.remote ? boardIdFromUrl(text) : null;
      if (linkedBoard) {
        createBoardCardAt(ctx, linkedBoard, viewCenter(pane));
        return;
      }
      if (looksLikeUrl(text)) {
        createLinkAt(ctx, text, viewCenter(pane));
        return;
      }
      // any other text lands as a sticky note
      createNoteAt(ctx, viewCenter(pane), false, text.slice(0, 5000));
    };

    // Copy/cut: fill the system clipboard with the items, so another tab (or a later session)
    // can paste them. Ctrl+C/X already snapshotted them a moment ago; a copy from elsewhere
    // (the browser menu) snapshots here. Viewers may copy; cutting needs edit rights.
    const onCopyCut = (e: ClipboardEvent) => {
      if (activePane !== pane || isTextTarget(e.target)) return;
      if (e.type === 'cut' && readOnly) return;
      let payload = recentClipboard(1500);
      if (!payload) {
        const ids = [...pane.getState().selection];
        if (!ids.length) return;
        payload = snapshotForClipboard(board, ids);
        rememberClipboard(payload);
        if (e.type === 'cut') deleteSelection(ctx);
      }
      writeToClipboardEvent(e, payload);
      e.preventDefault();
    };

    const onBlur = () => pane.getState().setSpaceHeld(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('paste', onPaste);
    window.addEventListener('copy', onCopyCut);
    window.addEventListener('cut', onCopyCut);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('copy', onCopyCut);
      window.removeEventListener('cut', onCopyCut);
      window.removeEventListener('blur', onBlur);
    };
  }, [board, pane, readOnly]);
}
